import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../shared/api/client';
import { useAuthStore } from '../shared/store/authStore';
import { Card } from '../shared/ui/Card';
import { ParkingDataTable } from '../shared/ui/ParkingDataTable';

export function AlprPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const canRead = useAuthStore(
    (s) => s.hasPermission('sessions:read') || s.hasPermission('api:access'),
  );
  const canWrite = useAuthStore(
    (s) => s.hasPermission('sessions:write') || s.hasPermission('api:access'),
  );
  const cameras = useQuery({
    queryKey: ['cameras'],
    queryFn: () => api.listCameras(),
    enabled: canRead,
  });
  const reads = useQuery({
    queryKey: ['plate-reads'],
    queryFn: () => api.listPlateReads(),
    enabled: canRead,
  });
  const [provider, setProvider] = useState<'openalpr' | 'platerecognizer'>('openalpr');
  const [imageUrl, setImageUrl] = useState('https://example.com/plate.jpg');

  const recognize = useMutation({
    mutationFn: () => api.recognizeAlpr({ imageUrl, provider }),
    onSuccess: async () => qc.invalidateQueries({ queryKey: ['plate-reads'] }),
  });

  if (!canRead) {
    return <div className="alert alert-warning">{t('alpr.forbidden')}</div>;
  }

  const cameraRows = cameras.data?.data ?? [];
  const readRows = reads.data?.data ?? [];

  return (
    <>
{canWrite && (
        <Card title={t('alpr.recognize')} className="mb-4">
          <div className="row g-3 align-items-end">
            <div className="col-md-3">
              <label className="form-label">{t('alpr.provider')}</label>
              <select
                className="form-select"
                value={provider}
                onChange={(e) => setProvider(e.target.value as 'openalpr' | 'platerecognizer')}
              >
                <option value="openalpr">OpenALPR</option>
                <option value="platerecognizer">Plate Recognizer</option>
              </select>
            </div>
            <div className="col-md-7">
              <label className="form-label">{t('alpr.imageUrl')}</label>
              <input
                className="form-control"
                value={imageUrl}
                onChange={(e) => setImageUrl(e.target.value)}
              />
            </div>
            <div className="col-md-2">
              <button
                type="button"
                className="btn btn-primary w-100"
                disabled={recognize.isPending}
                onClick={() => recognize.mutate()}
              >
                {t('alpr.recognize')}
              </button>
            </div>
          </div>
          {recognize.data && (
            <div className="alert alert-success mt-3 mb-0">
              {recognize.data.data.rawText} ({(recognize.data.data.confidence * 100).toFixed(0)}%)
            </div>
          )}
        </Card>
      )}

      <Card title={t('alpr.cameras')} className="mb-4">
        <ParkingDataTable
          storageKey="alpr-cameras"
          columns={[t('alpr.code'), t('alpr.name'), 'ALPR']}
          refreshKey={cameraRows.map((c) => c.id).join('|')}
          actionsColumn={false}
          galleryItems={cameraRows.map((c) => ({
            id: c.id,
            icon: 'bi-camera-video',
            iconTone: c.alprEnabled ? ('success' as const) : ('secondary' as const),
            title: c.code,
            subtitle: c.name,
            meta: c.alprEnabled ? 'ON' : 'OFF',
          }))}
        >
          {cameraRows.map((c) => (
            <tr key={c.id}>
              <td className="fw-semibold">{c.code}</td>
              <td>{c.name}</td>
              <td>{c.alprEnabled ? 'ON' : 'OFF'}</td>
            </tr>
          ))}
        </ParkingDataTable>
      </Card>

      <Card title={t('alpr.reads')}>
        <ParkingDataTable
          storageKey="alpr-reads"
          columns={[t('alpr.plate'), t('alpr.provider'), t('alpr.confidence'), t('alpr.when')]}
          refreshKey={readRows.map((r) => r.id).join('|')}
          order={[[3, 'desc']]}
          actionsColumn={false}
          galleryItems={readRows.map((r) => ({
            id: r.id,
            icon: 'bi-card-text',
            iconTone: 'info' as const,
            title: r.rawText,
            subtitle: r.provider,
            meta: `${(r.confidence * 100).toFixed(0)}% · ${new Date(r.createdAt).toLocaleString()}`,
          }))}
        >
          {readRows.map((r) => (
            <tr key={r.id}>
              <td className="fw-semibold">{r.rawText}</td>
              <td>{r.provider}</td>
              <td>{(r.confidence * 100).toFixed(0)}%</td>
              <td>{new Date(r.createdAt).toLocaleString()}</td>
            </tr>
          ))}
        </ParkingDataTable>
      </Card>
    </>
  );
}
